import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const produtos = [
  { icon: 'ri-armchair-line', title: 'Cadeiras Ergonômicas', desc: 'Frisokar e Plaxmetal NR-17 para escritórios e indústrias em Cabo de Santo Agostinho.', link: '/cadeiras' },
  { icon: 'ri-layout-grid-line', title: 'Mesas Corporativas', desc: 'Estações de trabalho e mesas de reunião Marzo Vitorino e Pandim.', link: '/mesas' },
  { icon: 'ri-layout-masonry-line', title: 'Divisórias Modulares', desc: 'Divisórias sem obra para escritórios no Cabo.', link: '/divisorias' },
  { icon: 'ri-volume-mute-line', title: 'Cabines Acústicas', desc: 'Isolamento de até 45dB para open offices.', link: '/cabines-acusticas' },
  { icon: 'ri-archive-line', title: 'Mobiliário de Aço', desc: 'Armários, estantes e lockers W3 para ambientes industriais.', link: '/aco' },
  { icon: 'ri-tools-line', title: 'Projetos Turnkey', desc: 'Projeto completo de mobiliário para sua empresa no Cabo.', link: '/turnkey' },
];

export default function MoveisCorporativosCaboPage() {
  useSEO({
    title: 'Móveis Corporativos Cabo de Santo Agostinho PE | Cadeiras e Mesas — Home Office Premium',
    description: 'Móveis corporativos em Cabo de Santo Agostinho PE com entrega e montagem. Cadeiras ergonômicas NR-17, mesas corporativas, divisórias e mobiliário de aço para empresas do Porto de Suape e RMR.',
    keywords: 'móveis corporativos Cabo de Santo Agostinho, cadeiras ergonômicas Cabo PE, mesas corporativas Cabo Pernambuco, mobiliário escritório Suape, cadeiras escritório Cabo, Frisokar Cabo de Santo Agostinho, divisórias Cabo PE, móveis corporativos Suape',
    path: '/moveis-corporativos-cabo-de-santo-agostinho',
    ogTitle: 'Móveis Corporativos Cabo de Santo Agostinho PE | Home Office Premium',
    ogDescription: 'Móveis corporativos em Cabo de Santo Agostinho PE com entrega e montagem. Cadeiras, mesas e divisórias para empresas do Suape.',
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    const schemaId = 'schema-cabo';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Home Office Premium — Móveis Corporativos Cabo de Santo Agostinho',
      description: 'Móveis corporativos em Cabo de Santo Agostinho PE. Cadeiras ergonômicas, mesas corporativas e divisórias para empresas do Porto de Suape e RMR.',
      url: `${siteUrl}/moveis-corporativos-cabo-de-santo-agostinho`,
      telephone: '+55-81-98203-0285',
      address: { '@type': 'PostalAddress', streetAddress: 'Av. Agamenon Magalhães, 1519', addressLocality: 'Caruaru', addressRegion: 'PE', postalCode: '55014-000', addressCountry: 'BR' },
      geo: { '@type': 'GeoCoordinates', latitude: -8.2897, longitude: -35.0339 },
      areaServed: [
        { '@type': 'City', name: 'Cabo de Santo Agostinho' },
        { '@type': 'City', name: 'Ipojuca' },
        { '@type': 'City', name: 'Jaboatão dos Guararapes' },
        { '@type': 'City', name: 'Recife' },
      ],
      serviceType: 'Venda e Instalação de Móveis Corporativos',
    });

    const faqId = 'schema-cabo-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'A Home Office Premium entrega móveis corporativos em Cabo de Santo Agostinho?', acceptedAnswer: { '@type': 'Answer', text: 'Sim. Entregamos e montamos móveis corporativos em Cabo de Santo Agostinho, incluindo empresas do Porto de Suape, Ipojuca e toda a RMR.' } },
        { '@type': 'Question', name: 'Vocês atendem empresas do Porto de Suape?', acceptedAnswer: { '@type': 'Answer', text: 'Sim. Atendemos empresas instaladas no Complexo Industrial Portuário de Suape, em Cabo de Santo Agostinho e Ipojuca, com entrega e montagem de móveis corporativos, cadeiras ergonômicas e mobiliário de aço.' } },
      ],
    });

    return () => { script?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="relative bg-[#111111] overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=modern%20industrial%20corporate%20office%20interior%20Suape%20port%20Pernambuco%20Brazil%20professional%20workspace%20ergonomic%20furniture%20contemporary%20design%20warm%20lighting&width=1440&height=600&seq=cabohero2026&orientation=landscape" alt="Móveis corporativos em Cabo de Santo Agostinho PE" className="w-full h-full object-cover object-top opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#E8610A]/20 border border-[#E8610A]/40 text-[#E8610A] text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-map-pin-line"></i>
                Cabo de Santo Agostinho — Porto de Suape
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
                Móveis Corporativos em<br />
                <span className="text-[#E8610A]">Cabo de Santo Agostinho</span>
              </h1>
              <p className="text-white/70 text-lg max-w-xl leading-relaxed mb-8">
                Cadeiras ergonômicas, mesas corporativas, divisórias e mobiliário de aço para empresas do Porto de Suape e região. Entrega e montagem inclusos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20do%20Cabo%20de%20Santo%20Agostinho%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-whatsapp-line"></i>Solicitar Orçamento
                </a>
                <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-message-3-line"></i>Falar com Especialista
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Móveis Corporativos para Cabo de Santo Agostinho</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {produtos.map(p => (
                <Link key={p.title} to={p.link} className="group bg-[#f8f8f6] rounded-2xl p-7 hover:bg-orange-50 transition-colors duration-300 cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5 group-hover:bg-[#E8610A] transition-colors duration-300">
                    <i className={`${p.icon} text-[#E8610A] text-xl group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base mb-2">{p.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="py-16 bg-[#E8610A]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Orçamento para Cabo de Santo Agostinho</h2>
            <p className="text-white/70 mb-8">Fale com nossa equipe e receba um projeto personalizado para sua empresa.</p>
            <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20do%20Cabo%20de%20Santo%20Agostinho%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#E8610A] font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
      <Footer />
      <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20do%20Cabo%20de%20Santo%20Agostinho%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp">
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
